import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import TimeEventTag from './TimeEventTag';
import AddTagDialog from './AddTagDialog';
import AddTagChoiceDialog from './AddTagChoiceDialog';
import { useWebSocket } from '../WebSocketContext';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import Divider from '@mui/material/Divider';
import { useParams } from 'react-router-dom';
import { Typography, Paper, Fade, Grow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SettingIntelligentTag from '../SettingIntelligentTag';
import { useDashboard } from '../Dashboard/DashboardContext';
import {
  isProjectReanalysisTerminalStatus,
  queueProjectReanalysis
} from '../../Utils/timeEventsApi';
import { isErrorStatus } from '../../constants/timeEventStatus';

// Utility function to validate and normalize tag objects
const asBoolean = (value) => (
  value === true ||
  value === 1 ||
  value === '1' ||
  String(value).toLowerCase() === 'true'
);

const normalizeTagText = (value, fallback = '') => (
  value === null || value === undefined ? fallback : String(value)
);

export const findTagByVariable = (tags, variable) => {
  const normalizedVariable = normalizeTagText(variable).trim();
  if (!normalizedVariable || !Array.isArray(tags)) {
    return null;
  }

  return tags.find((tag) => normalizeTagText(tag?.variable).trim() === normalizedVariable) || null;
};

export const getPriorityTagDisplayValue = (tags, priorityTagVariable) => {
  const priorityTag = findTagByVariable(tags, priorityTagVariable);
  const replyValue = priorityTag?.reply;

  if (replyValue === null || replyValue === undefined) {
    return '';
  }

  if (typeof replyValue === 'string') {
    return replyValue.trim();
  }

  if (typeof replyValue === 'number' || typeof replyValue === 'boolean') {
    return String(replyValue);
  }

  try {
    return JSON.stringify(replyValue);
  } catch (error) {
    return String(replyValue);
  }
};

export const hasDisplayableTagValue = (tag) => {
  if (!tag) {
    return false;
  }

  if (tag.coordinates) {
    return true;
  }

  if (String(tag.type ?? '').toLowerCase().includes('image')) {
    return true;
  }

  const replyValue = tag.reply == null ? '' : String(tag.reply);
  if (replyValue.trim() === '') {
    return false;
  }

  try {
    const parsedReply = JSON.parse(replyValue);
    if (Array.isArray(parsedReply) && parsedReply.length === 0) {
      return false;
    }
  } catch (error) {
    // Non-JSON values are displayable as long as they are not empty strings.
  }

  return true;
};

export const validateAndNormalizeTag = (tag) => {
  if (!tag) return null;
  //console.log("Received tag from server:", tag); // DEBUG
  
  return {
    id: tag.id ?? `tag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timeeventid: tag.timeeventid,
    tag_type: tag.tag_type || 'timeevent',
    variable: normalizeTagText(tag.variable || tag.name || `variable_${tag.id || Math.random().toString(36).substr(2, 9)}`),
    description: tag.description ?? '',
    reply: tag.reply ?? '',
    done: asBoolean(tag.done),
    icon: tag.icon ?? null, // Include icon field
    type: tag.type ?? 'text', // Default type to 'text' if not provided
    coordinates: tag.coordinates ?? null,
    confidence: tag.confidence ?? null,
    confidence_source: tag.confidence_source ?? null,
    needs_review: asBoolean(tag.needs_review),
    review_reason: tag.review_reason ?? '',
    loading: asBoolean(tag.loading) || (!asBoolean(tag.done) && !hasDisplayableTagValue(tag)),
  };
};

export const isTagLoading = (tag, timeevent = null) => {
  if (isErrorStatus(timeevent?.eventstatus)) {
    return false;
  }

  return asBoolean(tag?.loading) || (!asBoolean(tag?.done) && !hasDisplayableTagValue(tag));
};

export const shouldDisplayTag = (tag, visibleTags = null, timeevent = null) => {
  if (!tag) {
    return false;
  }

  const matchesVisibility = (
    !visibleTags ||
    visibleTags.length === 0 ||
    visibleTags.includes(tag.variable)
  );

  return matchesVisibility && (
    hasDisplayableTagValue(tag) ||
    isTagLoading(tag, timeevent) ||
    isErrorStatus(timeevent?.eventstatus)
  );
};

const TimeEventTags = ({
  timeevent,
  onToggleRenalyze,
  canManageProject = false,
  visibleTags = null,
  maxVisibleTags = null,
  enableAnimations = true,
  priorityTagVariable = null,
  onTagsChange = null
}) => {
  const { subscribeToMessages } = useWebSocket();
  const { t } = useTranslation();
  const { caseid } = useParams();
  const { tags: globalTags } = useDashboard();
  const [tags, setTags] = useState(
    Array.isArray(timeevent.customFields) ? timeevent.customFields : []
  );
  const normalizedMaxVisibleTags = Number.isFinite(maxVisibleTags) ? maxVisibleTags : null;
  const prioritizedTagVariable = (priorityTagVariable || '').trim();
  const effectiveMaxVisibleTags = prioritizedTagVariable ? 1 : normalizedMaxVisibleTags;
  const shouldLimitTags = Number.isFinite(effectiveMaxVisibleTags) && effectiveMaxVisibleTags > 0;
  const [showAllTags, setShowAllTags] = useState(!shouldLimitTags);
  const [openTagDialog, setOpenTagDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openAddTagChoiceDialog, setOpenAddTagChoiceDialog] = useState(false);
  const [tagsWereSaved, setTagsWereSaved] = useState(false);  
  const [openCreateTagDialog, setOpenCreateTagDialog] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isTagsVisible, setIsTagsVisible] = useState(false);
  const [isProjectReanalysisQueuing, setIsProjectReanalysisQueuing] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(['projectReanalysisStatus'], (message) => {
      const data = message.data || {};
      if (Number(data.project_id) !== Number(caseid)) {
        return;
      }
      if (isProjectReanalysisTerminalStatus(data.status)) {
        setIsProjectReanalysisQueuing(false);
      }
      if (data.status === 'failed' || data.status === 'completed_with_errors') {
        setSnackbar({
          open: true,
          message: data.message || t('settings.redo_analysis_error', 'Failed to rebuild analysis'),
          severity: 'error'
        });
        return;
      }
      if (data.status === 'completed') {
        setSnackbar({
          open: true,
          message: t('settings.redo_analysis_complete', 'Project reanalysis completed'),
          severity: 'success'
        });
      }
    });

    return unsubscribe;
  }, [caseid, subscribeToMessages, t]);

  // Update customFields when timeevent.customFields changes
  const timeeventId = timeevent?.id;
  useEffect(() => {
    // Ensure all tags have the required 'variable' field
    const validatedTags = (timeevent.customFields || [])
      .map(validateAndNormalizeTag)
      .filter(tag => tag !== null)
      .sort((a, b) => a.variable.localeCompare(b.variable)); // Sort tags alphabetically by variable name
    setTags(validatedTags);
    
    // Trigger animation when tags are loaded
    if (validatedTags.length > 0) {
      setIsTagsVisible(true);
    }
  }, [timeevent]);

  useEffect(() => {
    if (shouldLimitTags) {
      setShowAllTags(false);
    } else {
      setShowAllTags(true);
    }
  }, [shouldLimitTags, timeeventId, prioritizedTagVariable]);

  // Parse and set available tags from global tags
  useEffect(() => {
    if (globalTags) {
      try {
        const parsedTags = JSON.parse(globalTags);
        setAvailableTags(parsedTags || []);
      } catch (error) {
        console.error('Error parsing global tags:', error);
        setAvailableTags([]);
      }
    }
  }, [globalTags]);

  const handleDeleteVariable = (tagToDelete) => {
    if (!tagToDelete) {
      return;
    }

    const nextTags = tags.filter((field) => field.id !== tagToDelete.id);
    setTags(nextTags);
    onTagsChange?.(nextTags);

  };

  const handleUpdateVariable = (updatedField) => {
    // Ensure the updated field has a variable property
    const validatedField = validateAndNormalizeTag(updatedField);
    
    if (!validatedField) {
      console.error('Failed to validate updated field:', updatedField);
      return;
    }
    
    const nextTags = tags.map((field) => (
      field.id === validatedField.id ? validatedField : field
    ));
    setTags(nextTags);
    onTagsChange?.(nextTags);

  };

  const handleOpenTagManager = () => {
    if (!canManageProject) {
      return;
    }
    setOpenTagDialog(true);
    setTagsWereSaved(false);
  };

  const handleOpenAddTagChoice = () => {
    if (!canManageProject) {
      return;
    }
    setOpenAddTagChoiceDialog(true);
  };

  const handleCloseAddTagChoice = () => {
    setOpenAddTagChoiceDialog(false);
  };

  const handleChooseManualTag = () => {
    setOpenAddTagChoiceDialog(false);
    handleOpenCreateTag();
  };

  const handleChooseAiTag = () => {
    setOpenAddTagChoiceDialog(false);
    handleOpenTagManager();
  };

  const handleCloseTagManager = () => {
    // Only show the confirmation dialog if tags were actually saved during this session
    if (tagsWereSaved) {
      setOpenConfirmDialog(true);
    } else {
      handleFinalClose();
    }
  };

  const handleFinalClose = () => {
    setOpenTagDialog(false);
    setOpenConfirmDialog(false);
    setTagsWereSaved(false);
  };

  const handleTagSaved = () => {
    // This will be called when tags are saved in SettingIntelligentTag
    setTagsWereSaved(true);
  };

  const handleReloadAllDocuments = async () => {
    if (isProjectReanalysisQueuing) {
      return;
    }
    setIsProjectReanalysisQueuing(true);
    try {
      const result = await queueProjectReanalysis(caseid);
      setSnackbar({
        open: true,
        message: result?.deduplicated
          ? t('settings.redo_analysis_already_queued', 'Project reanalysis is already queued')
          : t('settings.redo_analysis_success', 'Rebuild queued successfully'),
        severity: 'success'
      });
      handleFinalClose();
    } catch (error) {
      setIsProjectReanalysisQueuing(false);
      setSnackbar({
        open: true,
        message: error?.message || t('settings.redo_analysis_error', 'Failed to rebuild analysis'),
        severity: 'error'
      });
    }
  };

  const handleReloadCurrentTimeEvent = () => {
    // Use the onToggleRenalyze prop instead of directly sending a WebSocket message
    if (onToggleRenalyze) {
      onToggleRenalyze();
    }
    handleFinalClose();
  };  // New tag creation handlers
  const handleOpenCreateTag = () => {
    setOpenCreateTagDialog(true);
  };
  const handleCloseCreateTag = () => {
    setOpenCreateTagDialog(false);
  };
  const handleTagCreated = (newTag) => {
    const normalizedTag = validateAndNormalizeTag(newTag);
    if (!normalizedTag) {
      return;
    }

    // Update local state and the owning time event with the new tag
    const nextTags = [...tags, normalizedTag];
    setTags(nextTags);
    onTagsChange?.(nextTags);
    setOpenCreateTagDialog(false);
    
    // Trigger animation for new tag
    if (!isTagsVisible) {
      setIsTagsVisible(true);
    }


  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const filteredTags = useMemo(() => (
    (Array.isArray(tags) ? tags : []).filter((variable) => shouldDisplayTag(variable, visibleTags, timeevent))
  ), [tags, visibleTags, timeevent]);

  const orderedTags = useMemo(() => {
    if (!prioritizedTagVariable) {
      return filteredTags;
    }
    const priorityIndex = filteredTags.findIndex((field) => {
      const variableName = (field.variable || '').trim().toLowerCase();
      return variableName && variableName === prioritizedTagVariable.toLowerCase();
    });

    if (priorityIndex <= 0) {
      return filteredTags;
    }

    const prioritized = filteredTags[priorityIndex];
    const rest = filteredTags.filter((_, index) => index !== priorityIndex);
    return [prioritized, ...rest];
  }, [filteredTags, prioritizedTagVariable]);

  const displayedTags = useMemo(() => {
    if (!shouldLimitTags || showAllTags) {
      return orderedTags;
    }
    return orderedTags.slice(0, effectiveMaxVisibleTags);
  }, [orderedTags, shouldLimitTags, showAllTags, effectiveMaxVisibleTags]);

  const totalHiddenTagCount = shouldLimitTags
    ? Math.max(orderedTags.length - effectiveMaxVisibleTags, 0)
    : 0;
  const hiddenTagCount = showAllTags
    ? 0
    : totalHiddenTagCount;
  const showTagToggle = shouldLimitTags && totalHiddenTagCount > 0;
  const useTagAnimations = enableAnimations && displayedTags.length <= 12;

  // Theme colors - matching Sidebar
  const primaryColor = '#5757b0';
  const manageTagsColor = '#6767c0';
  const hoverColor = '#7777d0';
  const lightPurple = '#f0f0ff';
  const actionTagTextSx = {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    lineHeight: 1,
  };
  const actionTagSx = (accentColor) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px 4px 6px',
    minHeight: '28px',
    borderRadius: '12px',
    borderLeft: `4px solid ${accentColor}`,
    background: `linear-gradient(135deg, #ffffff 0%, ${accentColor}12 100%)`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    marginRight: '8px',
    marginTop: '4px',
    marginBottom: '2px',
    color: accentColor,
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      background: `linear-gradient(135deg, #ffffff 0%, ${accentColor}22 100%)`,
      boxShadow: '0 2px 8px rgba(87, 87, 176, 0.14)',
    },
    '&:focus-visible': {
      outline: `2px solid ${accentColor}55`,
      outlineOffset: '2px',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  });
  return (
    <>      
      <Fade in={isTagsVisible || !useTagAnimations} timeout={useTagAnimations ? 600 : 0} appear={useTagAnimations}>
        <Box 
          display="flex" 
          flexWrap="wrap" 
          alignItems="center"
          sx={{
            minHeight: tags.length > 0 ? 'auto' : '40px',
            transition: useTagAnimations ? 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          {useTagAnimations ? (
            displayedTags.map((variable, index) => (
              <Grow
                key={`${timeevent.id}-${variable.variable || variable.id || index}`}
                in={true}
                timeout={{
                  enter: 400 + (index * 100), // Stagger animation
                  exit: 300,
                }}
                style={{
                  transformOrigin: '0 0',
                }}
              >
                <Box
                  sx={{
                    display: { xs: 'block', sm: 'inline-block' },
                    width: { xs: '100%', sm: 'auto' },
                    animation: `slideInUp 0.6s ease-out ${index * 0.1}s both`,
                    '@keyframes slideInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(20px) scale(0.8)',
                      },
                      '60%': {
                        opacity: 0.8,
                        transform: 'translateY(-2px) scale(1.05)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0px) scale(1)',
                      },
                    },
                  }}
                >
                <TimeEventTag
                  tag={variable}
                  timeevent={timeevent}
                  onDelete={handleDeleteVariable}
                  onUpdate={handleUpdateVariable}
                  canManageProject={canManageProject}
                  isProcessing={isTagLoading(variable, timeevent)}
                />
                </Box>
              </Grow>
            ))
          ) : (
            displayedTags.map((variable, index) => (
              <Box
                key={`${timeevent.id}-${variable.variable || variable.id || index}`}
                sx={{ display: { xs: 'block', sm: 'inline-block' }, width: { xs: '100%', sm: 'auto' } }}
              >
                <TimeEventTag
                  tag={variable}
                  timeevent={timeevent}
                  onDelete={handleDeleteVariable}
                  onUpdate={handleUpdateVariable}
                  canManageProject={canManageProject}
                  isProcessing={isTagLoading(variable, timeevent)}
                />
              </Box>
            ))
          )}

          {showTagToggle && !showAllTags && (
            <ButtonBase
              onClick={() => setShowAllTags(true)}
              sx={actionTagSx(manageTagsColor)}
              aria-label={t('timeevent.show_more_tags_label', {
                count: hiddenTagCount,
                defaultValue: `Show ${hiddenTagCount} more tags`
              })}
            >
              <Typography component="span" sx={actionTagTextSx}>
                {t('timeevent.show_more_tags', {
                  count: hiddenTagCount,
                  defaultValue: `+${hiddenTagCount} more`
                })}
              </Typography>
            </ButtonBase>
          )}
          {showTagToggle && showAllTags && (
            <ButtonBase
              onClick={() => setShowAllTags(false)}
              sx={actionTagSx(manageTagsColor)}
              aria-label={t('timeevent.show_less_tags_label', 'Show fewer tags')}
            >
              <Typography component="span" sx={actionTagTextSx}>
                {t('timeevent.show_less_tags', 'Show fewer')}
              </Typography>
            </ButtonBase>
          )}

          {/* Add new tag button */}
          {canManageProject && (
            <Grow 
              in={true} 
              timeout={useTagAnimations ? 800 : 0}
              appear={useTagAnimations}
              style={{ transformOrigin: '0 0' }}
            >
              <Tooltip title="Create New Tag for This Event" arrow>
                <ButtonBase
                  disableRipple
                  onClick={handleOpenAddTagChoice}
                  sx={actionTagSx(primaryColor)}
                  aria-label="Create new tag"
                >
                  <AddRoundedIcon />
                  <Typography component="span" sx={actionTagTextSx}>
                    Add
                  </Typography>
                </ButtonBase>
              </Tooltip>
            </Grow>
          )}

        </Box>
      </Fade>

      {/* Dialog to display the SettingIntelligentTag component */}
      <Dialog 
        open={openTagDialog} 
        onClose={handleCloseTagManager}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 5,
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: primaryColor, 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            py: 2
          }}
        >
          <ReplayIcon sx={{ mr: 1 }} />
          {t('settings.manage_intelligent_tags') || 'Manage Tags'}
        </DialogTitle>
        <DialogContent dividers>
          <SettingIntelligentTag 
            onTagsSaved={handleTagSaved} 
          />
          <DialogActions sx={{ p: 2, bgcolor: '#f9f9ff' }}>
            <Button 
              onClick={handleCloseTagManager}
              variant="contained"
              startIcon={<CancelOutlinedIcon />}
              sx={{
                backgroundColor: primaryColor,
                color: '#fff',
                '&:hover': { backgroundColor: '#7777d0' },
                py: 1,
                px: 3,
                borderRadius: 2,
                boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              {t('close') || 'Close'}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog when closing the tag manager */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            padding: 0,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontWeight: 'bold',
            color: 'white',
            bgcolor: primaryColor,
            py: 2.5,
            px: 3
          }}
        >
          <ReplayIcon style={{ marginRight: '12px', fontSize: '28px' }} />
          {t('settings.reload_tags_title') || 'Reload Tags?'}
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <DialogContentText sx={{ m: 3, color: '#444', fontSize: '1rem' }}>
            {t('settings.reload_tags_message') || 'Would you like to rebuild all documents with the new tags or only reanalyze this document?'}
          </DialogContentText>
          
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2, 
              p: 2.5, 
              backgroundColor: '#fff4f4', 
              borderRadius: 2, 
              border: '1px solid #ffe0e0',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <WarningAmberIcon style={{ color: '#d32f2f', marginRight: '12px', fontSize: '28px' }} />
            <DialogContentText sx={{ color: '#d32f2f', m: 0, fontWeight: 500 }}>
              {t('settings.reanalyze_warning') || 'Reanalyzing this document with AI will reset this event and erase all your edits.'}
            </DialogContentText>
          </Paper>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 4 }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 2.5, 
                width: '48%', 
                textAlign: 'center',
                borderRadius: 3,
                transition: 'all 0.2s ease',
                cursor: isProjectReanalysisQueuing ? 'default' : 'pointer',
                opacity: isProjectReanalysisQueuing ? 0.7 : 1,
                pointerEvents: isProjectReanalysisQueuing ? 'none' : 'auto',
                '&:hover': { 
                  transform: 'translateY(-5px)', 
                  boxShadow: '0 8px 15px rgba(0,0,0,0.1)' 
                },
                backgroundColor: primaryColor,
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
              onClick={handleReloadAllDocuments}
            >
              {isProjectReanalysisQueuing
                ? <CircularProgress size={40} sx={{ mb: 1, color: 'white' }} />
                : <RefreshIcon sx={{ fontSize: 40, mb: 1 }} />}
              <Typography variant="h6" noWrap={false} sx={{ mb: 1, fontWeight: 'medium' }}>
                {t('settings.redo_analysis') || 'Rebuild All'}
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: '100%' }}>
                Apply tags to all documents
              </Typography>
            </Paper>

            <Paper 
              elevation={3} 
              sx={{ 
                p: 2.5, 
                width: '48%', 
                textAlign: 'center',
                borderRadius: 3,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': { 
                  transform: 'translateY(-5px)', 
                  boxShadow: '0 8px 15px rgba(0,0,0,0.1)' 
                },
                backgroundColor: '#6767c0',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
              onClick={handleReloadCurrentTimeEvent}
            >
              <RefreshIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" noWrap={false} sx={{ mb: 1, fontWeight: 'medium' }}>
                {t('settings.reanalyze_this') || 'Reanalyze This Document'}
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: '100%' }}>
                Apply tags only to this document
              </Typography>
            </Paper>
          </Box>
        </DialogContent>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ padding: '16px', display: 'flex', justifyContent: 'center', bgcolor: '#f5f5ff' }}>
          <Button 
            onClick={handleFinalClose}
            variant="outlined"
            startIcon={<CancelOutlinedIcon />}
            sx={{ 
              borderColor: primaryColor,
              color: primaryColor,
              '&:hover': { backgroundColor: lightPurple },
              py: 1,
              px: 4,
              borderRadius: 2,
              fontWeight: 'medium'
            }}
          >
            
            {t('settings.tags_just_close') || 'This affect all new event only - Just Close'}
          </Button>
        </DialogActions>
      </Dialog>      {/* Add Tag Dialog */}
      <AddTagChoiceDialog
        open={openAddTagChoiceDialog}
        onClose={handleCloseAddTagChoice}
        onManual={handleChooseManualTag}
        onAi={handleChooseAiTag}
        primaryColor={primaryColor}
        hoverColor={hoverColor}
        manageTagsColor={manageTagsColor}
      />
      {/* Add Tag Dialog */}
      <AddTagDialog
        open={openCreateTagDialog}
        onClose={handleCloseCreateTag}
        onTagCreated={handleTagCreated}
        timeevent={timeevent}
        availableTags={availableTags}
        existingTags={tags}
        primaryColor={primaryColor}
      />{/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              fontSize: '24px',
            },
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 'medium',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TimeEventTags;
